import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import errorBoundary from '@ifeng-fe/errorBoundary';
import { QQShare } from '@ifeng-fe/three_terminal/es/mobile/qqShare/dynamic';
import Header from './Header';
import WebHeader from './WebHeader';
import Banner from './Banner';
import Tabs from './Tabs';
import Vote from './Vote';
import Map from './Map';
import States from './States';
import { refreshContent } from '@src/services/api';
import Share from './Share';
import { getRaceView } from './race';
import styles from './index.css';

const RACES = ['house', 'senate', 'governor'];
const SPIN_TURN = 800;
const AUTO_REFRESH_MS = 15 * 1000;
const CONTENT_KEYS = ['pageConfig', 'house', 'senate', 'governor', 'totals'];

const isPcClient = () => !/Android|iPhone|iPad|iPod|Mobile|Harmony|OpenHarmony|ArkWeb|Windows Phone/i.test(navigator.userAgent || '');

const wait = time => new Promise(resolve => setTimeout(resolve, time));

const afterPaint = () => new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

const isSameContent = (prev = {}, next = {}) =>
    CONTENT_KEYS.every(key => {
        const a = prev[key] || {};
        const b = next[key] || {};

        if (a.timestamp != null && b.timestamp != null) {
            return String(a.timestamp) === String(b.timestamp);
        }

        if (a.updateTime && b.updateTime) {
            return a.updateTime === b.updateTime;
        }

        return JSON.stringify(a) === JSON.stringify(b);
    });

const Layout = ({ content = {} }) => {
    const [tab, setTab] = useState(0);
    const [source, setSource] = useState(content);
    const [refreshing, setRefreshing] = useState(false);
    const [onlySwing, setOnlySwing] = useState(false);
    const sourceRef = useRef(source);
    const refreshingRef = useRef(false);
    sourceRef.current = source;
    const race = source[RACES[tab]] || {};
    const view = getRaceView(source, tab);
    const pageConfig = source.pageConfig || {};
    const pc = isPcClient();
    let header = <WebHeader />;

    if (pc) header = <WebHeader isPc={pc} />;
    else if (window.grounds) header = <Header share={pageConfig} />;

    const handleRefresh = async () => {
        if (refreshingRef.current) return;
        refreshingRef.current = true;
        setRefreshing(true);
        const request = refreshContent(sourceRef.current);
        try {
            await afterPaint();
            const started = performance.now();
            setSource(await request);
            const left = SPIN_TURN - (performance.now() - started);
            if (left > 0) await wait(left);
        } finally {
            refreshingRef.current = false;
            setRefreshing(false);
        }
    };

    useEffect(() => {
        let cancel = false;

        const poll = async () => {
            if (refreshingRef.current) return;

            try {
                const next = await refreshContent(sourceRef.current);
                if (cancel || isSameContent(sourceRef.current, next)) return;
                setSource(next);
            } catch (e) {
                // 自动刷新失败时保留当前数据
            }
        };

        const timer = setInterval(poll, AUTO_REFRESH_MS);

        return () => {
            cancel = true;
            clearInterval(timer);
        };
    }, []);
    return (
        <div className={`${styles.page} ${pc || window.grounds ? '' : styles.webPage}`}>
            {header}
            <Banner description={pageConfig.pagedesc} isPc={pc} />
            <Tabs current={tab} onChange={setTab} />
            <Vote type={view.type} total={view.total} majority={view.majority} dem={view.dem} gop={view.gop} winner={view.winner} noteLeft={view.noteLeft} noteRight={view.noteRight} />
            <div className={styles.box}>
                <Map type={tab} data={view.rows} />
                <States
                    type={tab}
                    data={view.rows}
                    updateTime={race.updateTime}
                    refreshing={refreshing}
                    onRefresh={handleRefresh}
                    onlySwing={onlySwing}
                    onOnlySwingChange={setOnlySwing}
                />
            </div>
            <Share source={source} tab={tab} />
            <QQShare
                content={{
                    url: pageConfig.shareurl,
                    title: pageConfig.title,
                    img: pageConfig.thumb || '',
                    desc: pageConfig.desc,
                    cb: () => {},
                }}
            />
        </div>
    );
};

Layout.propTypes = {
    content: PropTypes.object,
};

export default errorBoundary(Layout);
